
import { Vue, Options, prop } from "vue-class-component";
import Checkbox from "primevue/checkbox";
import { debounce } from "lodash";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { OptionsClass } from '@/domain/entities/MainApp';
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import { CargoV2Controller } from '@/app/ui/controllers/CargoV2Controller';
import { CargoV2ListClientParentApiRequest } from '@/data/payload/api/CargoV2ApiRequest';
import { getHubData } from '@/app/infrastructures/misc/Cookies';

class Props {
  isPlane = prop<boolean>({
    default: true,
    type: Boolean
  });
  transportType = prop<string>({
    default: "plane",
    type: String
  });
}

@Options({
  emits: ["filter"],
  components: {
    Checkbox,
    Skeleton
  }
})
class CargoV2FilterRtc extends Vue.with(Props) {
  form = {
    destination: [],
    client: [],
    hasMinimumWeight: false,
    hasMinimumPieces: false,
    sort: ""
  };

  // Destination Section
  isOpenDestination = false;
  destinationCityData: OptionsClass[] = [];
  destinationSearch = "";
  loadingInfiniteScroll = false;

  firstDestination = new OptionsClass({
    id: 0,
    label: "",
    code: "",
    isChecked: false
  });

  created() {
    this.firstDestination.label = this.$t("cargoV2.components.common.all");
    this.firstClient.label = this.$t("cargoV2.components.common.all");
  }

  toggleDestination() {
    this.destinationSearch = "";
    this.destinationCityData = [];
    this.isOpenDestination = !this.isOpenDestination;
    this.firstDestination.isChecked = this.form.destination.some(
      (item) => item === ""
    );
    if (this.isOpenDestination) {
      this.fetchDestinationCity();
    }
  }

  get destinationFilterTitle() {
    return `${
      this.form.destination.some((item) => item === "")
        ? this.$t("cargoV2.components.common.all")
        : this.form.destination.length
    } ${this.$t("cargoV2.components.filterRtc.destinationCitiesSelected")}`;
  }

  resetDestination() {
    this.form.destination = [];
    this.isOpenDestination = false;
    this.$emit("filter", this.form);
  }

  onSearchDestinationCity(e: any) {
    this.destinationSearch = e.target.value;
    this.fetchSearchDestinationCity();
  }

  fetchSearchDestinationCity = debounce(() => {
    this.destinationCityData = [];
    this.fetchDestinationCity();
  }, 250);

  fetchDestinationCity(page = 1, limit = 10, useInfiniteScroll = false) {
    this.loadingInfiniteScroll = useInfiniteScroll;
    LocationController.getCityList({
      search: this.destinationSearch,
      status: "",
      page: page,
      limit: limit
    });
  }

  get isLoadingCity() {
    return LocationController.isLoading;
  }

  get optionDestinationCity() {
    const newData = LocationController.cityData.cityData.map(
      (item) =>
        new OptionsClass({
          id: item.id,
          label: item.code,
          code: item.code,
          isChecked: this.form.destination.some((e) => e === item.code)
        })
    );
    this.destinationCityData = [...this.destinationCityData, ...newData];
    return [this.firstDestination, ...this.destinationCityData];
  }

  destinationPagination() {
    return LocationController.cityData.pagination.page;
  }

  fetchInfiniteScrollDestination(event: any) {
    const { scrollTop, scrollHeight, clientHeight } = event.target;

    const threshold = 50;

    if (
      scrollTop + clientHeight >= scrollHeight - threshold &&
      !this.isLoadingCity
    ) {
      this.fetchDestinationCity(
        LocationController.cityData.pagination.page + 1,
        LocationController.cityData.pagination.limit,
        true
      );
    }
  }

  onCheckDestination(value: boolean, item: OptionsClass) {
    if (item.id !== 0) {
      this.optionDestinationCity[0].isChecked = false;
    } else {
      this.optionDestinationCity
        .filter((e) => e.id)
        .forEach((e) => (e.isChecked = false));
    }
    const city = this.optionDestinationCity.find((e) => e.id === item.id);

    if (city) {
      city.isChecked = value;
    }
  }

  selectDestination() {
    this.form.destination = this.optionDestinationCity
      .filter((item) => item.isChecked)
      .map((item) => item.code) as [];
    this.isOpenDestination = false;
    this.$emit("filter", this.form);
  }

  // Client Section
  isOpenClient = false;
  clientData: OptionsClass[] = [];
  clientSearch = "";
  firstClient = new OptionsClass({
    id: 0,
    label: "",
    code: "",
    isChecked: false
  });

  toggleClient() {
    this.clientSearch = "";
    this.clientData = [];
    this.firstClient.isChecked = this.form.client.some((item) => item === 0);
    this.isOpenClient = !this.isOpenClient;
    if (this.isOpenClient) {
      this.fetchClientParent();
    }
  }

  fetchClientParent(page = 1, limit = 10) {
    const hubId = Number(getHubData()?.hubId || 0);
    const params = new CargoV2ListClientParentApiRequest({
      search: this.clientSearch,
      page: page,
      limit: limit,
      transportType: this.transportType,
      hubId: hubId
    });
    CargoV2Controller.getRTCListClientParent(params);
  }

  get clientFilterTitle() {
    return `${
      this.form.client.some((item) => item === 0)
        ? this.$t("cargoV2.components.common.all")
        : this.form.client.length
    } ${this.$t("cargoV2.components.filterRtc.clientsSelected")}`;
  }

  resetClient() {
    this.form.client = [];
    this.isOpenClient = false;
    this.$emit("filter", this.form);
  }

  get isLoadingClientPartner() {
    return CargoV2Controller.isLoadingClient;
  }

  onSearchClient(e: any) {
    this.clientSearch = e.target.value;
    this.fetchSearchClient();
  }

  fetchSearchClient = debounce(() => {
    this.clientData = [];
    this.fetchClientParent();
  }, 250);

  get clientParentData() {
    return CargoV2Controller.clientParentData;
  }

  get optionClientParentData() {
    const newData = this.clientParentData.map(
      (item) =>
        new OptionsClass({
          id: item.id,
          label: item.name,
          isChecked: this.form.client.some((e) => e === item.id)
        })
    );
    this.clientData = [...this.clientData, ...newData];
    return [this.firstClient, ...this.clientData];
  }

  onCheckClient(value: boolean, item: OptionsClass) {
    if (item.id !== 0) {
      this.optionClientParentData[0].isChecked = false;
    } else {
      this.optionClientParentData
        .filter((e) => e.id)
        .forEach((e) => (e.isChecked = false));
    }
    const client = this.optionClientParentData.find((e) => e.id === item.id);

    if (client) {
      client.isChecked = value;
    }
  }

  selectClient() {
    this.form.client = this.optionClientParentData
      .filter((item) => item.isChecked)
      .map((item) => item.id) as [];
    this.isOpenClient = false;
    this.$emit("filter", this.form);
  }

  // Miminum section
  isOpenMinimum = false;
  tempHasMinimumWeight = false;
  tempHasMinimumPieces = false;

  toggleMinimum() {
    this.isOpenMinimum = !this.isOpenMinimum;
    this.tempHasMinimumWeight = this.form.hasMinimumWeight;
    this.tempHasMinimumPieces = this.form.hasMinimumPieces;
  }

  selectMinimum() {
    this.form.hasMinimumWeight = this.tempHasMinimumWeight;
    this.form.hasMinimumPieces = this.tempHasMinimumPieces;
    this.toggleMinimum();
    this.$emit("filter", this.form);
  }

  get minimumFilterTitle() {
    return this.form.hasMinimumPieces && this.form.hasMinimumWeight
      ? this.$t("cargoV2.components.filterRtc.minimumWeightAndPieces")
      : this.form.hasMinimumPieces
      ? this.$t("cargoV2.components.filterRtc.minimumPieces")
      : this.form.hasMinimumWeight
      ? this.$t("cargoV2.components.filterRtc.minimumWeight")
      : this.$t("cargoV2.components.filterRtc.selectMinimumLimit");
  }

  resetMinimum() {
    this.form.hasMinimumPieces = false;
    this.form.hasMinimumWeight = false;
    this.isOpenMinimum = false;
    this.$emit("filter", this.form);
  }

  // Sort section
  isOpenSort = false;

  toggleSort() {
    this.isOpenSort = !this.isOpenSort;
  }

  selectSort(sort: string) {
    this.form.sort = sort;
    this.toggleSort();
    this.$emit("filter", this.form);
  }

  get sortFilterTitle() {
    return this.form.sort === "terdekat"
      ? this.$t("cargoV2.components.filterRtc.cotNearest")
      : this.form.sort === "terlama"
      ? this.$t("cargoV2.components.filterRtc.cotLongest")
      : this.$t("cargoV2.components.searchFlightFilter.sort");
  }

  resetSort() {
    this.form.sort = "";
    this.$emit("filter", this.form);
  }
}
export default CargoV2FilterRtc;
